import React, {useCallback} from 'react';
import {useTranslation} from 'react-i18next';
import EditorLayout from '../../../../components/Layouts/EditorLayout';
import SMCTextInput from '../../../../components/Simple/SMCTextInput';
import {SystemSettingsGetSchema} from '../../../../api/DBSchema/SystemSettings';
import SMCLabel from '../../../../components/Simple/SMCLabel';
import RowLayout from '../../../../components/Layouts/RowLayout';
import SMCNumberInput from '../../../../components/Simple/SMCNumberInput';
import {dateAndTimeToString, enqueueFileDownload, getAxiosFilename} from '../../../../utils/utils';
import SMCButton from '../../../../components/Simple/SMCButton';
import Sepperator from '../../../../components/Simple/Sepperator';
import Api from '../../../../api/SMPFetchHelper';
import {useSMCErrorHandler} from '../../../../utils/errorUtils';
import {useSMCSnackbar} from '../../../../utils/hooks';

const pageSchema = SystemSettingsGetSchema.pages.certificateAuthority;
const pageId = pageSchema.pageId;

function SystemSettingsCertificateAuthority({
    systemSettingsObject,
    setSystemSettingsData,
}) {
    const { t } = useTranslation();

    const pageData = systemSettingsObject[pageId];
    const setPageValue = useCallback((paramName, value) => {
        setSystemSettingsData([pageId, paramName], value);
    }, [setSystemSettingsData]);

    const rootCertExpire             = pageData[pageSchema.rootCertExpire];
    const autoCertRenew              = pageData[pageSchema.autoCertRenew];
    const expiresDate                = pageData[pageSchema.expiresDate];
    const isCaCertificateInitialized = pageData[pageSchema.isCaCertificateInitialized];
    const fingerPrint                = pageData[pageSchema.fingerPrint];
    const gwCertExpire               = pageData[pageSchema.gwCertExpire];
    const gwCertRenew                = pageData[pageSchema.gwCertRenew];
    const revocationPeriod           = pageData[pageSchema.revocationPeriod];

    const { getErrorMessage } = useSMCErrorHandler();
    const { enqueueErrorSnackbar } = useSMCSnackbar();

    const dowloadCACert = useCallback(() => {
        Api.getCaCert()
            .then(resp => {
                const fileName = getAxiosFilename(resp) || 'smp.crt';
                enqueueFileDownload(resp.data, fileName);
            })
            .catch(e => {
                const errorCode = getErrorMessage(e);
                enqueueErrorSnackbar(t(errorCode));
            })
    }, [enqueueErrorSnackbar, getErrorMessage, t]);


    return (
        <EditorLayout>
            <SMCLabel bold>{t('SMC_SETTINGS_CERT_ROOT_HEAD')}</SMCLabel>
            {/* Root certificate expires */}
            <RowLayout>
                <SMCNumberInput
                    value={rootCertExpire}
                    onChange={e => setPageValue(pageSchema.rootCertExpire, parseInt(e.target.value))}
                    label={t('SMC_SETTINGS_CERT_ROOT_EXPIRES')}
                    width={100}
                    inline
                    disabled
                />
                <SMCLabel inline>{t('SMC_SETTINGS_CERT_ROOT_EXPIRES_MONTHS')}</SMCLabel>
            </RowLayout>
            {/* Root certificate auto-renew */}
            <RowLayout>
                <SMCNumberInput
                    value={autoCertRenew}
                    onChange={e => setPageValue(pageSchema.autoCertRenew, parseInt(e.target.value))}
                    label={t('SMC_SETTINGS_CERT_ROOT_RENEW')}
                    width={100}
                    disabled
                />
                <SMCLabel>{t('SMC_SETTINGS_CERT_ROOT_RENEW_MONTHS')}</SMCLabel>
            </RowLayout>
            {/* Experation date */}
            <SMCTextInput
                readOnly
                value={isCaCertificateInitialized ? dateAndTimeToString(expiresDate) : t('SMC_SETTINGS_CERT_ROOT_NOTINIT')}
                label={t('SMC_SETTINGS_CERT_ROOT_WILLEXPIRE')}
            />
            {/* Fingerprint */}
            <SMCTextInput
                readOnly
                value={isCaCertificateInitialized ? fingerPrint : t('SMC_SETTINGS_CERT_ROOT_NOTINIT')}
                label={t('SMC_SETTINGS_CERT_ROOT_FINGERPRINT')}
            />
            <RowLayout>
              {/* remove the 'Reinitailize CA' button to avoid case of generate root CA by mistake - this option is avaiable from API [SMP-4089]*/}
                {/* <SMCButton onClick={openReinitializeCaModal}>{t('SMC_SETTINGS_CERT_ROOT_BTN2')}</SMCButton> */}
                <SMCLabel useContextWidth />
                {
                    isCaCertificateInitialized && (
                        <SMCButton onClick={dowloadCACert}>{t('SMC_SETTINGS_CERT_EXP_CA')}</SMCButton>
                    )
                }
            </RowLayout>
            <Sepperator />
            <SMCLabel bold>{t('SMC_SETTINGS_CERT_GW_HEAD')}</SMCLabel>
            {/* Gateway certificate expires */}
            <RowLayout>
                <SMCNumberInput
                    value={gwCertExpire}
                    onChange={e => setPageValue(pageSchema.gwCertExpire, parseInt(e.target.value))}
                    label={t('SMC_SETTINGS_CERT_GW_EXPIRES')}
                    width={100}
                    disabled
                />
                <SMCLabel>{t('SMC_SETTINGS_CERT_GW_EXPIRES_MONTHS')}</SMCLabel>
            </RowLayout>
            {/* Gateway certificate auto-renew */}
            <RowLayout>
                <SMCNumberInput
                    value={gwCertRenew}
                    onChange={e => setPageValue(pageSchema.gwCertRenew, parseInt(e.target.value))}
                    label={t('SMC_SETTINGS_CERT_GW_RENEW')}
                    width={100}
                    disabled
                />
                <SMCLabel>{t('SMC_SETTINGS_CERT_GW_RENEW_MONTHS')}</SMCLabel>
            </RowLayout>
            {/* remove the 'Regenerate certificates for all gateways' button to avoid case of generate certificate for all gateways by mistake - this option is avaiable from API [SMP-4089]*/}
            {/* <RowLayout>
                <SMCLabel useContextWidth />
                <SMCButton onClick={openReinitializeCaModal}>{t('SMP.SETTINGS.CERT_AUTH.LINE3')}</SMCButton>
            </RowLayout> */}
            <Sepperator />
            <SMCLabel bold>{t('SMP.SETTINGS.CERT_AUTH.CERT_REVOCATION_LIST')}</SMCLabel>
            {/* CRL valid time */}
            <RowLayout>
                <SMCNumberInput
                    value={revocationPeriod}
                    onChange={e => setPageValue(pageSchema.revocationPeriod, parseInt(e.target.value))}
                    label={t('SMP.SETTINGS.CERT_AUTH.LINE4')}
                    width={100}
                    disabled
                />
                <SMCLabel>{t('SMP.SETTINGS.CERT_AUTH.HOURS')}</SMCLabel>
            </RowLayout>
            {/* remove the 'Configure CRL verification behavior' button this option is avaiable from API [SMP-4089]*/}
            {/* <RowLayout>
                <SMCLabel useContextWidth />
                <SMCButton onClick={openGatewaysCrlModal}>{t('SMP.SETTINGS.CERT_AUTH.LINE5')}</SMCButton>
            </RowLayout>
            <ReinitializeCaModal
                isOpen={isReinitializeCaModalOpen}
                onRequestClose={() => {
                    closeReinitializeCaModal();
                    refreshAction();
                }}
                isCertificateInitialized={isCaCertificateInitialized}
            /> */}
            {/* <GatewaysCRLModal
                isOpen={isGatewaysCrlModalOpen}
                onRequestClose={() => {
                    closeGatewaysCrlModal();
                    refreshAction();
                }}
            /> */}
        </EditorLayout>
    )
}

export default SystemSettingsCertificateAuthority;